import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import ButtonComponent from "components/button/ButtonComponent";
import ExportButton from "components/button/ExportButton";
import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import ImageComponent from "components/image/ImageComponent";
import LabeledRadioBox from "components/Label/LabeledRadioBox";
import routes from "constants/routes";
import { SESSION_KEY } from "constants/key";
import { TOption } from "type";
import useMaker from "../hook/useMaker";
import useCarModel from "../hook/useCarModel";
import useTireSize from "../hook/useTireSize";
import { TireSizeRequest } from "../api/carModelApi";
import useUpTireSize, { getKeyValueFromId } from "../hook/useUpTireSize";
import { ICarTireSizeItem, ITireSizeItem } from "../types";
import MessageModal from "../components/MessageModal";

interface CarTypeSelectorProps {
  type: "model" | "wheel";
}

interface SelectOptionsProps {
  onSelect: (option: string) => void;
  options: TOption[];
  value: string | number;
  label: string;
}

const styles = {
  selectLabel: {
    fontSize: { sm: "16px", md: "20px", xl: "25px" },
    textAlign: "center",
    paddingX: { sm: "16px", md: "24px", xl: 0 },
  },
  title: {
    fontWeight: "regular",
    color: "var(--black)",
    fontSize: { sm: "22px", md: "28px", xl: "34px" },
  },
  titleUpload: {
    fontSize: { sm: "18px", md: "20px", xl: "25px" },
  },
  textUpload: {
    fontSize: { sm: "14px", md: "16px", xl: "23px" },
  },
  titleSubBottom: {
    fontSize: { xs: "16px", md: "18px", xl: "20px" },
  },
  labelBox: {
    width: "203px",
    height: "48px",
    display: "flex",
    alignItems: "center",
    backgroundColor: "var(--primary-500)",
  },
  select: {
    width: "239px",
    height: "50px",
    padding: "10px",
    borderRadius: "9px",
  },
  button: {
    width: "287px",
    marginX: "auto",
    marginTop: 8,
  },
  header: {
    fontWeight: "medium",
    color: "var(--gray-700)",
    marginBottom: 2.5,
  },
  container: {
    padding: 2,
    p: { sm: 2, lg: 7 },
    display: { sm: "block", lg: "flex" },
  },
  mainContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: { sm: "16px", lg: "0" },
  },
  selectionContainer: {
    display: "flex",
    flexDirection: "column",
    rowGap: "17px",
    marginY: "45px",
  },
  tireSizeContainer: {
    display: "flex",
    flexDirection: "column",
    rowGap: 2.5,
    background: "var(--offwhite-100)",
    padding: 1,
    borderRadius: "6",
  },
  divider: {
    width: "100%",
    marginBottom: { sm: 2, lg: 4 },
    marginTop: { sm: 4, lg: 5 },
  },
  upTireSizeGroup: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    columnGap: "6px",
    alignItems: "start",
    marginTop: "50px",
    width: "100%",
    justifyContent: "center",
  },
  tireSelectionButton: {
    marginTop: "80px",
    marginBottom: "40px",
  },
};

const SelectOptions = memo(({ label, onSelect, value, options }: SelectOptionsProps) => {
  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      onSelect(event.target.value as string);
    },
    [onSelect],
  );

  const safeValue = useMemo(
    () => (options.some((el) => el.value === value) ? value : ""),
    [options, value],
  );

  return (
    <Box sx={{ display: "flex", direction: "row", gap: "16px", alignItems: "center" }}>
      <Box sx={styles.labelBox}>
        <Typography
          sx={{ ml: 2, fontWeight: "bold", color: "var(--black)", ...styles.titleSubBottom }}
        >
          {label}
        </Typography>
      </Box>
      <Select sx={styles.select} onChange={handleChange} value={safeValue as never}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
});

SelectOptions.displayName = "SelectOptions";

interface TireSizeCardProps {
  carTireSize: ICarTireSizeItem;
  chosenTireSize: number | null;
  setChosenTireSize: (value: number) => void;
  setTireSizeList: () => void;
}

const TireSizeCard = memo(
  ({ carTireSize, chosenTireSize, setChosenTireSize, setTireSizeList }: TireSizeCardProps) => {
    const handleTireSizeChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setChosenTireSize(Number(e.target.value));
        setTireSizeList();
      },
      [setChosenTireSize],
    );

    return (
      <Box
        sx={{
          display: "flex",
          position: "relative",
          background: "var(--white)",
          gap: "50px",
          p: 2,
        }}
      >
        <ImageComponent
          objectFit="contain"
          alt={`${carTireSize.car_model_and_period}_image`}
          src={carTireSize.car_image_url}
          sx={{ width: "100%", height: "160px" }}
        />
        <Box sx={{ minWidth: { sm: "280px", lg: "350px" } }}>
          <Typography
            sx={{
              fontWeight: "bold",
              color: "var(--gray-700)",
              pl: 1,
              ...styles.titleSubBottom,
            }}
          >
            {carTireSize.car_model_and_period}
          </Typography>
          <FormControl>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ArrowDropDownIcon sx={{ fontSize: "32px" }} />
              <FormLabel
                id="demo-controlled-radio-buttons-group"
                sx={{
                  fontWeight: "bold",
                  color: "var(--gray-700) !important",
                  ...styles.titleSubBottom,
                }}
              >
                現状装着タイヤサイズ
              </FormLabel>
            </Box>
            <RadioGroup
              aria-labelledby="controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              sx={{ pl: 2 }}
              value={chosenTireSize}
              onChange={handleTireSizeChange}
            >
              {carTireSize.tiresizes.map((size: ITireSizeItem) => (
                <FormControlLabel
                  key={size.car_tiresize_id}
                  value={size.car_tiresize_id}
                  sx={{ color: "var(--gray-400)" }}
                  control={
                    <Radio
                      size="small"
                      sx={{
                        "&.Mui-checked": {
                          color: "var(--black)",
                        },
                      }}
                    />
                  }
                  label={size.car_tiresize}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>
    );
  },
);

TireSizeCard.displayName = "TireSizeCard";

/**
 * CarTypeSelector component allows users to select car models and tire sizes,
 * and proceed to the next step based on their selection.
 *
 * Props:
 *   - type: "model" | "wheel"
 *
 * Features:
 *   - Select car brand and model.
 *   - Display available tire sizes for the selected model.
 *   - Allow selection of up-tire sizes.
 *   - Handles navigation and session storage for selected options.
 *   - If `type` is "wheel", prompts user to choose between tire or wheel selection.
 */
export default function CarTypeSelector({ type }: CarTypeSelectorProps) {
  const isWheel = type === "wheel";

  const [paramsSizeTire, setParamsSizeTire] = useState<TireSizeRequest>({
    car_brand: "",
    car_model: -1,
  });
  const [chosenTireSize, setChosenTireSize] = useState<number | null>(null);
  const [carTireSizeId, setCarTireSizeId] = useState<number | null>(null);
  const [chosenUpTireSize, setChosenUpTireSize] = useState<number | null>(null);
  const [visibleNotification, setVisibleNotification] = useState(false);

  const [chooseTireOrWheel, setChooseTireOrWheel] = useState(false); // for isWheel

  const navigate = useNavigate();

  // Data fetching hooks
  const { makerList } = useMaker();
  const { carModelList } = useCarModel(paramsSizeTire.car_brand);
  const { tireSizeList } = useTireSize(paramsSizeTire);
  const { upTireSizeList, setTireSizeList } = useUpTireSize(carTireSizeId as number);

  // Memoized values
  const makerOptions = useMemo(
    () =>
      makerList.map((maker) => ({
        value: maker.brnd_label,
        label: maker.brnd_name,
      })),
    [makerList],
  );

  const carModelOptions = useMemo(
    () =>
      carModelList.map((model) => ({
        value: model.modl_code,
        label: model.modl_name,
      })),
    [carModelList],
  );

  // Handlers
  const handleNavigateQuotation = () => navigate(routes.quoteList);

  const handleNavigate = useCallback(
    (selectedType?: "tire" | "wheel"): void => {
      if (chosenTireSize === null || chosenUpTireSize === null) {
        setVisibleNotification(true);
        return;
      }

      const result = getKeyValueFromId(upTireSizeList, chosenUpTireSize, chosenTireSize);

      const tireSizes = upTireSizeList.flatMap((item) => item.sizes);
      const selectedTire = tireSizes.find((item) => item.mmm_idx === chosenUpTireSize);

      const selectedMaker = tireSizeList.find((item) =>
        item.tiresizes.find((tire) => tire.car_tiresize_id === chosenTireSize),
      );
      if (selectedMaker) {
        selectedMaker.selectedTireSize = selectedMaker.tiresizes.find(
          (tire) => tire.car_tiresize_id === chosenTireSize,
        );
        selectedMaker.selectedTire = selectedTire;
      }

      if (selectedTire) {
        const tires = [
          {
            tire_width: selectedTire?.mmm_tire_width,
            tire_rim_diam: String(selectedTire?.mmm_inch),
            tire_aspect_ratio: selectedTire?.mmm_tire_flat_rate,
          },
        ];
        sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_FROM, "maker");
        sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_MAKER, JSON.stringify(selectedMaker));
        if (!isWheel || (isWheel && selectedType === "tire")) {
          sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_TIRE, JSON.stringify(tires));
          sessionStorage.removeItem(SESSION_KEY.SELECTED_TIRE);
        } else {
          sessionStorage.setItem(SESSION_KEY.NEW_QUOTE_WHEEL, JSON.stringify(tires));
        }
      }

      // if (!isWheel) {
      // sessionStorage.setItem(SESSION_KEY.SELECTED_TIRE, JSON.stringify(result));
      //   sessionStorage.removeItem(SESSION_KEY.SELECTED_TIRE);
      // }
      sessionStorage.removeItem(SESSION_KEY.SELECTED_MAKER);
      sessionStorage.removeItem(SESSION_KEY.SELECTED_CATEGORY);
      sessionStorage.removeItem(SESSION_KEY.SET_TIRE_SELECTED_MAKER);
      sessionStorage.removeItem(SESSION_KEY.SET_TIRE_SELECTED_CATEGORY);
      sessionStorage.removeItem(SESSION_KEY.WHEEL_SELECTED_MAKER);
      sessionStorage.removeItem(SESSION_KEY.WHEEL_SELECTED_CATEGORY);

      // redirect by type and selectedType
      const route = !isWheel
        ? routes.mstProcFee
        : isWheel && selectedType === "tire"
          ? routes.mstSelectedTireProcFee
          : routes.mstWheelStore;

      // Clear wheel store state and selected wheel when navigating to wheel store (fresh flow)
      sessionStorage.removeItem(SESSION_KEY.WHEEL_STORE_STATE);
      sessionStorage.removeItem(SESSION_KEY.SELECTED_WHEEL);
      sessionStorage.removeItem(SESSION_KEY.SELECTED_TIRE);
      sessionStorage.removeItem(SESSION_KEY.MST_PROC_WHEEL_FEE);

      navigate(route);
    },
    [navigate, chosenTireSize, chosenUpTireSize],
  );

  const handleSelect = useCallback(
    (selected: string | number, selectType: "car_brand" | "car_model") => {
      const isCarBrand = selectType === "car_brand";
      setParamsSizeTire((prev) => ({
        car_brand: isCarBrand ? (selected as string) : prev.car_brand,
        car_model: isCarBrand ? -1 : (selected as number),
      }));
      setChosenTireSize(null);
      if (isCarBrand) return;
      setCarTireSizeId(null);
    },
    [],
  );

  const onSearchByCarTireSizeId = useCallback(() => {
    if (chosenTireSize === null) {
      setVisibleNotification(true);
      return;
    }
    setChosenUpTireSize(null);
    setCarTireSizeId(chosenTireSize);
  }, [chosenTireSize]);

  const handleUpTireSizeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setChosenUpTireSize(Number(e.target.value));
  }, []);

  const showTireSizes = useMemo(
    () => paramsSizeTire.car_model !== -1 && tireSizeList?.length > 0,
    [paramsSizeTire.car_model, tireSizeList],
  );

  const showUpTireSizes = useMemo(
    () => upTireSizeList?.length > 0 && chosenTireSize !== null && carTireSizeId !== null,
    [upTireSizeList, chosenTireSize, carTireSizeId],
  );

  // If isWheel show どちらから選択しますか？
  const handleChooseTireOrWheel = useCallback(() => {
    if (chosenTireSize === null || chosenUpTireSize === null) {
      setVisibleNotification(true);
      return;
    }

    setChooseTireOrWheel(true);
  }, [chosenUpTireSize, chosenTireSize]);

  /**
   * Handles the redirect logic based on the `isWheel` flag.
   * If `isWheel` is true, prompts the user to choose between tire or wheel selection.
   * Otherwise, proceeds with the default navigation.
   */
  const onDecideRedirect = useCallback(() => {
    if (isWheel) {
      handleChooseTireOrWheel();
    } else handleNavigate();
  }, [type, handleNavigate, handleChooseTireOrWheel]);

  useEffect(() => {
    if (
      chosenUpTireSize === null &&
      upTireSizeList?.length > 0 &&
      upTireSizeList[0]?.sizes?.length > 0
    ) {
      setChosenUpTireSize(upTireSizeList[0].sizes[0].mmm_idx);
    }
  }, [upTireSizeList, chosenUpTireSize]);

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={styles.container}>
        <Box>
          <Typography sx={{ ...styles.titleUpload, ...styles.header }}>新規見積</Typography>
          <ExportButton
            sx={{ ...styles.textUpload }}
            label="見積一覧"
            onClick={handleNavigateQuotation}
          />
        </Box>

        <Box sx={styles.mainContent}>
          <Typography sx={{ ...styles.title }}>車種から選ぶ</Typography>

          <Box sx={styles.selectionContainer}>
            <SelectOptions
              label="メーカー"
              value={paramsSizeTire.car_brand}
              options={makerOptions}
              onSelect={(value) => handleSelect(value, "car_brand")}
            />
            <SelectOptions
              label="車種"
              value={paramsSizeTire.car_model}
              options={carModelOptions}
              onSelect={(value) => handleSelect(value, "car_model")}
            />
          </Box>

          {showTireSizes && (
            <>
              <Box sx={styles.tireSizeContainer}>
                {tireSizeList.map((carTireSize) => (
                  <TireSizeCard
                    key={carTireSize.car_fmc}
                    carTireSize={carTireSize}
                    chosenTireSize={chosenTireSize}
                    setChosenTireSize={setChosenTireSize}
                    setTireSizeList={() => setTireSizeList([])}
                  />
                ))}
              </Box>

              <ButtonComponent
                theme="dark"
                label="決定する"
                sx={{ ...styles.button, ...styles.titleUpload }}
                onClick={onSearchByCarTireSizeId}
              />
            </>
          )}

          {showUpTireSizes && (
            <Box
              sx={{
                position: "sticky",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bottom: 0,
                zIndex: 10,
                background: "var(--white)",
                padding: 2,
              }}
            >
              <Divider sx={{ width: "100%", marginBottom: 2, marginTop: 4 }} />
              <Typography sx={styles.title}>サイズを指定します</Typography>
              <RadioGroup
                aria-labelledby="controlled-up-tire-size-group"
                name="up-tire-size"
                sx={styles.upTireSizeGroup}
                value={chosenUpTireSize}
                onChange={handleUpTireSizeChange}
              >
                {upTireSizeList.map((option) => (
                  <LabeledRadioBox
                    key={option.id}
                    option={option.sizes}
                    label={option.label}
                    sx={{ flexDirection: "column", alignItems: "center" }}
                    sxLabel={{
                      minWidth: { xs: 190, md: 230, lg: 300 },
                      width: { sm: `calc((100% - 16px)/${upTireSizeList.length})` },
                      justifyContent: "center",
                    }}
                  />
                ))}
              </RadioGroup>
              <ButtonComponent
                theme="dark"
                label="商品選択へ進む"
                sx={{
                  ...styles.button,
                  ...styles.titleUpload,
                  ...styles.tireSelectionButton,
                }}
                onClick={onDecideRedirect}
              />

              {chooseTireOrWheel && (
                <>
                  <Divider sx={styles.divider} />
                  <Typography sx={styles.title}>どちらから選択しますか？</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      mt: 8,
                      gap: { xs: 3, md: 4, lg: 10 },
                      pb: 2,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <ButtonComponent
                        theme="dark"
                        label="タイヤから選ぶ"
                        sx={{ ...styles.button, ...styles.titleUpload, mt: 0 }}
                        onClick={() => handleNavigate("tire")}
                      />
                      <Typography sx={{ fontSize: { xs: 12, lg: 14 }, mt: 1 }}>
                        ※タイヤ4種類＋ホイール4種類の組合せ選択可能
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <ButtonComponent
                        theme="dark"
                        label="ホイールから選ぶ"
                        sx={{ ...styles.button, ...styles.titleUpload, mt: 0 }}
                        onClick={() => handleNavigate("wheel")}
                      />
                      <Typography sx={{ fontSize: { xs: 12, lg: 14 }, mt: 1 }}>
                        ※ホイール1種類＋タイヤ4種類の組合せ選択可能
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <MessageModal
        message="タイヤサイズを選択してください"
        visibleMessage={visibleNotification}
        setVisibleMessage={setVisibleNotification}
      />
    </>
  );
}
